import { z } from 'zod';

import { makeFeatureFlagPayloadValidator } from './utils';

/**
 * How long live (websocket) transcription runs per recording segment.
 *
 * - `windowSeconds: null` — no window: live runs for the whole segment. This is
 *   the behavior shipping today and the fail-safe default: an absent, disabled,
 *   or unparseable flag lands here, so a missing or malformed payload can never
 *   be worse than the status quo. **Disabling the flag is the kill switch.**
 * - `windowSeconds: 300` — live runs 300s from segment start (re-armed on every
 *   pause/resume), then shuts down cleanly and the chunked-batch pipeline
 *   carries the rest of the transcript.
 *
 * One field, deliberately. A separate `mode` would be derivable from this one —
 * a window either exists or it does not — and every combination we cannot
 * express is a way a session cannot be misconfigured.
 *
 * Zero is not a configuration. "No live transcription at all" has never shipped
 * on web, and as a kill switch it is strictly worse than disabling the flag:
 * disabling restores known-good production behavior, whereas zero would put
 * users in a state that has never run in production. Zero — like any other
 * invalid payload — fails the parse and falls back to `null`. [ENG-5538]
 */
const liveTranscriptionWindowSchema = z.object({
  windowSeconds: z.number().int().positive().nullable(),
});

export const liveTranscriptionWindow = makeFeatureFlagPayloadValidator<
  z.infer<typeof liveTranscriptionWindowSchema>
>({
  defaultEnabled: false,
  schema: liveTranscriptionWindowSchema,
  defaultValue: {
    windowSeconds: null,
  },
});
